/**
 * One ask — the case agent asking a person a question, or for an approval —
 * with the answer form its kind and form take: words or an option (with a
 * typed value where the option takes one), an identity verdict, a search run
 * by hand, an approval (the budget ask with the new threshold); a question
 * in words or a search may be declined as not needed, saying why. Shared by
 * the queue's preview and Needs you's fallback: an ask is answered, never
 * resolved blind, and the answer resumes the run waiting on it. A rework (a
 * partner's return sent back) is previewed here and sent from Partner work
 * on the case, with the message as the person edits it. A question in words
 * or a search may be answered with a file, attached here.
 */

import { useState } from 'react';

import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  NumberInput,
  Radio,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowRight, IconHandStop } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { timeAgo } from '@/components/Inbox/timeAgo';
import { ApiConflictError, ApiValidationError, vnextApiClient } from '@/services/vnext-api';
import {
  Intervention,
  INTERVENTION_LABELS,
  InterventionAnswer,
  interventionTarget,
  NOT_THEM_REASONS,
  SECTION_NAMES,
  type IdentityVerdict,
  type NotThemReason,
} from '@/types/workbench';
import { toError } from '@/utils/errors';
import { AgentText } from './AgentText';
import { AttachFile, type AttachedFile } from './panes/AttachFile';
import { NotNeeded } from './panes/NotNeeded';

interface InterventionCardProps {
  intervention: Intervention;
  reportId: string;
  /** Called once the answer is recorded (refetch what shows it). */
  onAnswered: () => void | Promise<void>;
  /** Open the checklist item or the record it's about. */
  onOpenTarget?: (target: { type: 'checklist' | 'evidence'; id: string }) => void;
  /** Open Setup, where partner work is approved. */
  onOpenSetup?: () => void;
  /** Open an ask on the case page (a rework is sent from Partner work there). */
  onOpenAsk?: (interventionId: string) => void;
  focused?: boolean;
}

type Decision = IdentityVerdict['decision'];

/** A person's answers to "is this about them?", as the workbench words them. */
export const IDENTITY_ANSWERS: { label: string; value: Decision }[] = [
  { label: 'About them', value: 'same' },
  { label: 'About a connection', value: 'connected' },
  { label: 'Not them', value: 'different' },
  { label: 'Not sure', value: 'uncertain' },
];

function contextLines(intervention: Intervention): string[] {
  const { filename, reason } = intervention.payload as { filename?: unknown; reason?: unknown };
  return [filename, reason].filter((v): v is string => typeof v === 'string' && v.length > 0);
}

/** The API's answer to a failed resolve, in words. */
export function failureMessage(err: unknown): string {
  if (err instanceof ApiConflictError) {
    return err.detail.reason === 'use_setup'
      ? 'Partner work is approved in Setup, with the partner and the terms.'
      : 'This was already answered or closed — the list will refresh.';
  }
  if (err instanceof ApiValidationError) {
    return "That answer doesn't fit what was asked.";
  }
  return toError(err, 'Could not record the answer').message;
}

function listOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** A message's first lines, for a preview. */
export function firstLines(text: unknown, count: number): string | null {
  if (typeof text !== 'string' || text.trim() === '') {
    return null;
  }
  const lines = text.split('\n').filter((line) => line.trim() !== '');
  return lines.length > count ? `${lines.slice(0, count).join('\n')} …` : lines.join('\n');
}

function split(text: string): string[] {
  return text
    .split(/[;\n]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** A search research couldn't run (a state register, a court docket), run by
 * hand: where, for which names, what came back. */
function SearchForm({
  reportId,
  intervention,
  submitting,
  onSubmit,
}: Readonly<{
  reportId: string;
  intervention: Intervention;
  submitting: boolean;
  onSubmit: (answer: InterventionAnswer) => Promise<void>;
}>) {
  const [sources, setSources] = useState(listOf(intervention.payload.sources).join('; '));
  const [names, setNames] = useState(listOf(intervention.payload.names).join('; '));
  const [result, setResult] = useState<'none_found' | 'found' | ''>('');
  const [details, setDetails] = useState('');
  const [searchedOn, setSearchedOn] = useState(new Date().toISOString().slice(0, 10));
  const [limits, setLimits] = useState('');
  const [attached, setAttached] = useState<AttachedFile | null>(null);
  const searched =
    split(sources).length > 0 &&
    split(names).length > 0 &&
    result !== '' &&
    (result === 'none_found' || details.trim().length > 0);
  const ready = searched || attached !== null;

  const record = () =>
    void onSubmit({
      ...(searched && result
        ? {
            search: {
              sources: split(sources),
              names: split(names),
              result,
              details: details.trim() || null,
              searched_on: searchedOn || null,
              limits: limits.trim() || null,
            },
          }
        : {}),
      ...(attached ? { conversation_attachment_id: attached.id } : {}),
    });

  return (
    <Stack gap={6} data-testid="search-form">
      <TextInput
        size="xs"
        label="Searched"
        description="The registers or dockets, separated by semicolons"
        value={sources}
        onChange={(e) => setSources(e.currentTarget.value)}
        data-testid="search-sources"
      />
      <TextInput
        size="xs"
        label="For"
        description="The names searched, separated by semicolons"
        value={names}
        onChange={(e) => setNames(e.currentTarget.value)}
        data-testid="search-names"
      />
      <SegmentedControl
        size="xs"
        value={result}
        onChange={(v) => setResult(v as 'none_found' | 'found')}
        data={[
          { label: 'Nothing found', value: 'none_found' },
          { label: 'Found something', value: 'found' },
        ]}
        style={{ alignSelf: 'flex-start' }}
        data-testid="search-result"
      />
      {result === 'found' && (
        <Textarea
          size="xs"
          label="What was found"
          value={details}
          onChange={(e) => setDetails(e.currentTarget.value)}
          autosize
          minRows={2}
          data-testid="search-details"
        />
      )}
      <Group gap="xs" grow>
        <TextInput
          size="xs"
          type="date"
          label="Searched on"
          value={searchedOn}
          onChange={(e) => setSearchedOn(e.currentTarget.value)}
        />
        <TextInput
          size="xs"
          label="Limits"
          placeholder="e.g. trading names only"
          value={limits}
          onChange={(e) => setLimits(e.currentTarget.value)}
        />
      </Group>
      <Group gap={6} wrap="nowrap">
        <Button
          size="xs"
          disabled={!ready}
          loading={submitting}
          onClick={record}
          data-testid="answer-submit"
        >
          Record the search
        </Button>
        <AttachFile reportId={reportId} attached={attached} onChange={setAttached} />
      </Group>
    </Stack>
  );
}

export function InterventionCard({
  intervention,
  reportId,
  onAnswered,
  onOpenTarget,
  onOpenSetup,
  onOpenAsk,
  focused = false,
}: Readonly<InterventionCardProps>) {
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [choice, setChoice] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState<NotThemReason | null>(null);
  const [value, setValue] = useState('');
  const [budget, setBudget] = useState<number | string>('');
  const [attached, setAttached] = useState<AttachedFile | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const target = interventionTarget(intervention);
  const typed = intervention.options.find((option) => option.id === choice)?.takesText ?? false;
  const context = contextLines(intervention);
  const url = typeof intervention.payload.url === 'string' ? intervention.payload.url : null;
  const budgetAsk = intervention.target.type === 'budget';
  const partnerWork = intervention.target.type === 'partner_work';
  const rework = intervention.target.type === 'rework';
  const question = intervention.kind === 'question';
  const preview = rework ? firstLines(intervention.payload.message, 3) : null;
  const toCorrect = rework ? listOf(intervention.payload.issues).length : 0;

  const submit = async (answer: InterventionAnswer) => {
    setSubmitting(true);
    try {
      // A note typed on the card goes with the answer's own, if it has one;
      // the file attached goes with a question's answer (never a decline).
      const said = [answer.note, note.trim()].filter(Boolean).join(' — ');
      const given: InterventionAnswer =
        attached && !('not_needed' in answer)
          ? { ...answer, conversation_attachment_id: attached.id }
          : answer;
      await vnextApiClient.resolveIntervention(
        reportId,
        intervention.id,
        said ? { ...given, note: said } : given
      );
      notifications.show({ color: 'teal', message: 'Answer recorded — the work carries on.' });
      await onAnswered();
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({ color: 'red', message: failureMessage(err) });
      if (err instanceof ApiConflictError && err.detail.reason !== 'use_setup') {
        await onAnswered();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      data-testid={`intervention-${intervention.id}`}
      data-focused={focused || undefined}
      style={focused ? { borderColor: 'var(--mantine-color-orange-5)' } : undefined}
    >
      <Stack gap="xs">
        <Group gap={8} wrap="nowrap">
          <IconHandStop size={16} color="var(--mantine-color-orange-6)" />
          <Badge size="sm" variant="light" color="orange" style={{ textTransform: 'none' }}>
            {INTERVENTION_LABELS[intervention.kind]} · {SECTION_NAMES[intervention.section]}
          </Badge>
          {intervention.waitingTaskId && (
            <Text size="xs" c="dimmed">
              A run is waiting on this
            </Text>
          )}
          <Text size="xs" c="dimmed" ml="auto">
            {timeAgo(intervention.createdAt)}
          </Text>
        </Group>

        <Text size="sm" style={{ whiteSpace: 'pre-line' }} data-testid="intervention-question">
          <AgentText text={intervention.question ?? 'The case agent needs you.'} />
        </Text>
        {context.map((line) => (
          <Text key={line} size="xs" c="dimmed">
            <AgentText text={line} />
          </Text>
        ))}
        {url && (
          <Anchor href={url} target="_blank" rel="noopener noreferrer" size="xs">
            {url}
          </Anchor>
        )}
        {target && onOpenTarget && (
          <Anchor
            component="button"
            type="button"
            size="xs"
            onClick={() => onOpenTarget(target)}
            style={{ alignSelf: 'flex-start' }}
          >
            <Group gap={4}>
              Go to the {target.type === 'checklist' ? 'checklist item' : 'record'}
              <IconArrowRight size={12} />
            </Group>
          </Anchor>
        )}

        {showNote && (
          <Textarea
            size="xs"
            placeholder="A note with your answer (optional)"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            autosize
            minRows={2}
            data-testid="answer-note"
          />
        )}

        {intervention.kind === 'approval' && partnerWork && (
          <Stack gap={6} data-testid="answer-partner-work">
            <Text size="xs" c="dimmed">
              Partner work is approved in Setup, with the partner and the terms.
            </Text>
            <Group gap="xs">
              {onOpenSetup && (
                <Button size="xs" onClick={onOpenSetup} data-testid="answer-open-setup">
                  Open Setup
                </Button>
              )}
              <Button
                size="xs"
                variant="default"
                loading={submitting}
                onClick={() => void submit({ approved: false })}
                data-testid="answer-reject"
              >
                No partner for this
              </Button>
            </Group>
          </Stack>
        )}

        {intervention.kind === 'approval' && rework && (
          <Stack gap={6} data-testid="answer-rework">
            {preview && (
              <Text size="xs" style={{ whiteSpace: 'pre-line' }} data-testid="rework-preview">
                <AgentText text={preview} />
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {toCorrect > 0
                ? `${toCorrect} ${toCorrect === 1 ? 'thing' : 'things'} to correct · `
                : ''}
              Sent to the partner — or not — from Partner work on the case, with the message as you
              edit it.
            </Text>
            {onOpenAsk && (
              <Button
                size="xs"
                onClick={() => onOpenAsk(intervention.id)}
                style={{ alignSelf: 'flex-start' }}
                data-testid="answer-open-rework"
              >
                Open Partner work
              </Button>
            )}
          </Stack>
        )}

        {intervention.kind === 'approval' && !partnerWork && !rework && (
          <Group gap="xs" align="flex-end">
            {budgetAsk && (
              <NumberInput
                size="xs"
                w={150}
                label="Raise the threshold to, $"
                min={1}
                step={5}
                value={budget}
                onChange={setBudget}
                data-testid="answer-budget"
              />
            )}
            <Button
              size="xs"
              loading={submitting}
              disabled={budgetAsk && !(Number(budget) > 0)}
              onClick={() =>
                void submit(
                  budgetAsk ? { approved: true, budget_usd: Number(budget) } : { approved: true }
                )
              }
              data-testid="answer-approve"
            >
              {budgetAsk ? 'Raise it' : 'Approve'}
            </Button>
            <Button
              size="xs"
              variant="default"
              loading={submitting}
              onClick={() => void submit({ approved: false })}
              data-testid="answer-reject"
            >
              {budgetAsk ? 'Stop here' : "Don't approve"}
            </Button>
          </Group>
        )}

        {question && intervention.form === 'identity' && (
          <Group gap="xs" align="center">
            <SegmentedControl
              size="xs"
              value={decision ?? ''}
              onChange={(v) => setDecision(v as Decision)}
              data={IDENTITY_ANSWERS}
              data-testid="answer-identity"
            />
            {/* "Not them" says why, in the identity check's words. */}
            {decision === 'different' && (
              <Select
                size="xs"
                w={190}
                placeholder="Why not them?"
                aria-label="Why not them"
                data={NOT_THEM_REASONS.map((r) => ({ value: r.value, label: r.label }))}
                value={reason}
                onChange={(v) => setReason(v as NotThemReason | null)}
                data-testid="answer-reason"
              />
            )}
            <Button
              size="xs"
              disabled={decision === null || (decision === 'different' && reason === null)}
              loading={submitting}
              onClick={() =>
                decision &&
                void submit({
                  identity:
                    decision === 'different' && reason ? { decision, reason } : { decision },
                })
              }
              data-testid="answer-submit"
            >
              Record
            </Button>
          </Group>
        )}

        {question && intervention.form === 'search' && (
          <Stack gap="xs">
            <SearchForm
              reportId={reportId}
              intervention={intervention}
              submitting={submitting}
              onSubmit={submit}
            />
            <NotNeeded onDecline={(why) => submit({ not_needed: true, note: why })} />
          </Stack>
        )}

        {question && intervention.form === null && (
          <Stack gap="xs">
            {intervention.options.length > 0 ? (
              <Radio.Group value={choice} onChange={setChoice}>
                <Stack gap={6}>
                  {intervention.options.map((option) => (
                    <Radio
                      key={option.id}
                      value={option.id}
                      label={option.label}
                      size="xs"
                      data-testid={`answer-option-${option.id}`}
                    />
                  ))}
                </Stack>
              </Radio.Group>
            ) : (
              <Textarea
                size="xs"
                placeholder="Your answer"
                value={text}
                onChange={(e) => setText(e.currentTarget.value)}
                autosize
                minRows={2}
                data-testid="answer-text"
              />
            )}
            {typed && (
              <TextInput
                size="xs"
                placeholder="Type it"
                value={value}
                onChange={(e) => setValue(e.currentTarget.value)}
                data-testid="answer-typed"
              />
            )}
            <AttachFile reportId={reportId} attached={attached} onChange={setAttached} />
            <Button
              size="xs"
              disabled={
                intervention.options.length > 0
                  ? !choice || (typed && !value.trim())
                  : !text.trim() && attached === null
              }
              loading={submitting}
              onClick={() =>
                void submit(
                  intervention.options.length > 0
                    ? typed
                      ? { choice: choice ?? '', value: value.trim() }
                      : { choice: choice ?? '' }
                    : text.trim()
                      ? { text: text.trim() }
                      : {}
                )
              }
              style={{ alignSelf: 'flex-start' }}
              data-testid="answer-submit"
            >
              Answer
            </Button>
            {intervention.options.length === 0 && (
              <NotNeeded onDecline={(why) => submit({ not_needed: true, note: why })} />
            )}
          </Stack>
        )}

        {!showNote && (
          <Anchor
            component="button"
            type="button"
            size="xs"
            c="dimmed"
            onClick={() => setShowNote(true)}
            style={{ alignSelf: 'flex-start' }}
          >
            Add a note
          </Anchor>
        )}
      </Stack>
    </Card>
  );
}
